import { applyParentChildToggle, filterOptionsByQuery } from '../../utils';
import React, { useState, useMemo, useEffect } from 'react';
import { Checkbox, Stack, Box, Flex, TextInput, ScrollArea, Text, Highlight, Avatar } from '@mantine/core';
import {IconChevronDown, IconChevronRight, IconListSearch, IconSearch} from '@tabler/icons';
import { ActiveFilter, FilterRendererProps } from '../../types';
import { DomainHeaderIcon, ProductAreaHeaderIcon } from '../../../../../icons/x-symbol-svgrepo-com';
import { capitalizeFirstLetter } from '../../../../../utils/capitalizeFirstLetter';
import { getInitials } from '../../../../../utils/getInitials';
import { SourceIcon } from '../../../../../utils/SourceIcon';
import {
  groupReleaseOptions,
  isReleaseGroupOptionValue,
} from '../../../../../utils/releaseOptions';
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";


const CheckboxFilterForModal: React.FC<FilterRendererProps> = ({
  filterConfig,
  filterName,
  selectedCategory,
  localActiveFilters,
  setLocalActiveFilters,
  onCountUpdate,
}) => {
  const options = filterConfig.options || [];
  const [openPaths, setOpenPaths] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [freeTextValue, setFreeTextValue] = useState<string>('');
  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id;
  
  // Determine the correct filterObject for finding existing filters
  const filterObjectForLookup = filterConfig.filterObject || 'evidence';
  
  // Get current selections for this filter
  const existingFilter = localActiveFilters.find(
    filter => filter.name === filterName && filter.filterObject === filterObjectForLookup
  );
  const selectedValues = existingFilter?.selected || [];

  // Check if this is a product-area filter that needs tree rendering
  const isProductAreaTree = filterName === 'product-area';

  /**
   * Releases nest under their group here exactly as they do in the sticky
   * trigger and in `FilterTypeRenderers/CheckboxFilter`. Detected from the
   * *option data*, not the filter name: `buildReleaseOptions` is the only
   * thing in the app that puts `releaseGroupId` on an option, so the check
   * cannot collide with another filter, and no third hardcoded name joins
   * `product-area` in this file.
   */
  const isReleaseTree = useMemo(() => {
    const flat = Array.isArray(options) ? options : Object.values(options || {});
    return flat.some((option: any) => !!option?.releaseGroupId);
  }, [options]);

  /**
   * Everything below — chevrons, indent, cascading tick, the tree layout
   * itself — keys off this rather than off a filter name. Only *how to nest*
   * stays shape-specific: grouping by `domain` and grouping by
   * `releaseGroupId` are genuinely different operations.
   */
  const isTree = isProductAreaTree || isReleaseTree;
  
  const isOwnerFilter = filterName === 'owner' || filterConfig?.name === 'owner' || filterConfig?.dbFieldPath === 'ownerId';

  const isSourceFilter = filterConfig?.dbFieldPath?.endsWith('.origin');

  // Process options: flat, unless the filter is one of the nested shapes.
  const processedOptions = useMemo(() => {
    if (!isTree) {
      // Regular options - convert to array format
      let processed: any[] = [];
      
      if (Array.isArray(options)) {
        processed = options.map((opt: any) => ({
          label:
            (typeof opt?.label === 'string'
              ? opt.label
              : (typeof opt?.name === 'string'
                ? opt.name
                : (typeof opt === 'string' ? opt : ''))),
          value:
            (typeof opt?.value === 'string' || typeof opt?.value === 'number'
              ? String(opt.value)
              : (typeof opt?._id === 'string'
                ? opt._id
                : (typeof opt === 'string' ? opt : ''))),
          childComponents: opt.childComponents || [],
          // Preserve email and picture for owner filter
          email: opt.email,
          picture: opt.picture,
          name:
            (typeof opt?.name === 'string'
              ? opt.name
              : (typeof opt?.label === 'string' ? opt.label : '')),
          _id: opt._id,
        }));
      } else if (typeof options === 'object') {
        processed = Object.entries(options).map(([key, value]: [string, any]) => ({
          label:
            (typeof value?.label === 'string'
              ? value.label
              : (typeof value?.name === 'string' ? value.name : key)),
          value:
            (typeof value?.value === 'string' || typeof value?.value === 'number'
              ? String(value.value)
              : (typeof value?._id === 'string' ? value._id : key)),
          childComponents: value.childComponents || [],
          // Preserve email and picture for owner filter
          email: value.email,
          picture: value.picture,
          name:
            (typeof value?.name === 'string'
              ? value.name
              : (typeof value?.label === 'string' ? value.label : key)),
          _id: value._id || key,
        }));
      }
      
      // For owner filter: sort alphabetically and add "Me" option
      if (isOwnerFilter && processed.length > 0) {
        // Separate "Unassigned" if it exists
        const unassignedOption = processed.find((opt: any) => opt.value === 'unassigned');
        const otherOptions = processed.filter((opt: any) => opt.value !== 'unassigned');
        
        // Sort alphabetically by label/name
        const sortedOptions = otherOptions.sort((a, b) => {
          const labelA = String(a.label || a.name || '').toLowerCase();
          const labelB = String(b.label || b.name || '').toLowerCase();
          return labelA.localeCompare(labelB);
        });
        
        // Add "Me" option if current user exists in the list
        const meOptionIndex = sortedOptions.findIndex((opt: any) => 
          opt._id === currentUserId || opt.value === currentUserId
        );
        
        const result: any[] = [];
        
        // Add "Unassigned" first if it exists
        if (unassignedOption) {
          result.push(unassignedOption);
        }
        
        // Add "Me" option at the top (after Unassigned)
        if (meOptionIndex !== -1 && currentUserId) {
          const meOption = sortedOptions[meOptionIndex];
          result.push({
            ...meOption,
            label: `Me (${meOption.label})`,
            isMe: true,
          });
          // Remove from sorted list
          sortedOptions.splice(meOptionIndex, 1);
        }
        
        // Add remaining sorted options
        result.push(...sortedOptions);
        
        return result;
      }
      
      return processed;
    }

    // Releases: group by release group, keeping the incoming order inside each
    // group (`buildReleaseOptions` already sorts newest release first).
    if (isReleaseTree) {
      const flat = Array.isArray(options) ? options : Object.values(options || {});
      return groupReleaseOptions(flat as any[]);
    }

    // Product-area: group by domain
    const productAreas = Array.isArray(options) ? options : Object.values(options);
    const domainMap: { [domain: string]: any[] } = {};
    
    productAreas.forEach((pa: any) => {
      const domain = pa.domain || 'Unassigned';
      if (!domainMap[domain]) domainMap[domain] = [];
      domainMap[domain].push(pa);
    });

    return Object.entries(domainMap).map(([domain, childComponents]) => ({
      label: domain,
      value: domain,
      type: 'domain',
      childComponents,
    }));
  }, [options, isProductAreaTree, isReleaseTree, isTree]);

  /**
   * A release group is a synthetic parent, not a release: its value is
   * `release-group:<id>`, which the filter registration's `selectedIsObjId`
   * would hand to the backend as an ObjectId cast. It is therefore never
   * stored — a group shows ticked when every one of its releases is (derived
   * on the way in, stripped on the way out). Product-area domains are left
   * alone: a domain name *is* a value the backend matches on.
   */
  const withDerivedGroupTicks = (values: string[]): string[] => {
    if (!isReleaseTree) return values;

    const result = [...values];
    processedOptions.forEach((group: any) => {
      const children = (group?.childComponents || []).map((child: any) =>
        String(child?.value || child?.label || '').toLowerCase()
      );
      if (children.length === 0) return;
      const groupValue = String(group?.value || '').toLowerCase();
      const allTicked = children.every((child: string) => result.includes(child));
      if (allTicked && !result.includes(groupValue)) {
        result.push(groupValue);
      }
    });
    return result;
  };

  const withoutGroupValues = (values: any[]): any[] =>
    isReleaseTree
      ? values.filter((value) => !isReleaseGroupOptionValue(String(value)))
      : values;

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return processedOptions;
    
    return filterOptionsByQuery(processedOptions, searchQuery, isOwnerFilter);
  }, [processedOptions, searchQuery, isOwnerFilter]);

  // Auto-open paths when searching
  const autoOpenPaths = useMemo(() => {
    if (!searchQuery || !isTree) return new Set<string>();
    
    const collectParentPaths = (opts: any[], parentPath: string[] = [], acc: Set<string> = new Set()): Set<string> => {
      if (!opts || !Array.isArray(opts)) return acc;
      for (const option of opts) {
        if (!option) continue;
        const valueStr = String(option.value || option.label || '').toLowerCase();
        const labelStr = String(option.label || option.name || '').toLowerCase();
        const currentPath = [
          ...parentPath,
          valueStr,
        ];
        const pathKey = currentPath.join('>');
        const lowerQuery = searchQuery.toLowerCase();
        
        if (labelStr.includes(lowerQuery)) {
          for (let i = 1; i < currentPath.length; ++i) {
            acc.add(currentPath.slice(0, i).join('>'));
          }
        }
        if (option.childComponents && option.childComponents.length > 0) {
          collectParentPaths(option.childComponents, currentPath, acc);
        }
      }
      return acc;
    };
    
    return collectParentPaths(filteredOptions);
  }, [filteredOptions, searchQuery, isTree]);

  const toggleOpen = (path: string) => {
    setOpenPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handleOptionChange = (optionValue: string, checked: boolean) => {
    let newSelection: string[];
    
    if (checked) {
      newSelection = [...(Array.isArray(selectedValues) ? selectedValues : []), optionValue];
    } else {
      newSelection = (Array.isArray(selectedValues) ? selectedValues : []).filter(
        val => val !== optionValue
      );
    }

    if (isProductAreaTree) {
      newSelection = applyParentChildToggle(
        processedOptions,
        newSelection,
        optionValue,
        checked
      );
    }

    // Determine the correct filterObject: if filterConfig has filterObject, use it, otherwise use selectedCategory
    // For evidence filters, selectedCategory might be the filter name (like "product-area"), 
    // but filterObject should be "evidence" or "idea" based on the data structure
    const filterObject = filterConfig.filterObject || 'evidence';
    
    // Update local active filters
    const updatedFilter: ActiveFilter = {
      ...filterConfig,
      name: filterName,
      filterObject: filterObject as 'evidence' | 'idea',
      selected: newSelection,
      query: null,
      options: undefined,
    };

    setLocalActiveFilters(prev => {
      const existingIndex = prev.findIndex(
        filter => filter.name === filterName && filter.filterObject === filterObject
      );

      let updatedFilters;
      if (existingIndex >= 0) {
        if (newSelection.length === 0) {
          updatedFilters = prev.filter((_, index) => index !== existingIndex);
        } else {
          updatedFilters = [...prev];
          updatedFilters[existingIndex] = updatedFilter;
        }
      } else if (newSelection.length > 0) {
        updatedFilters = [...prev, updatedFilter];
      } else {
        updatedFilters = prev;
      }

      return updatedFilters;
    });

    // Update count
    onCountUpdate?.(newSelection.length);
  };

  // Recursive render function for tree structure
  const renderOptions = (optionsToRender: any[], parentPath: string[] = []) => {
    if (!optionsToRender || !Array.isArray(optionsToRender)) return null;

    return optionsToRender.filter(opt => opt != null).map((option: any, index: number) => {
      const optionValue = String(option.value || option.label || '').toLowerCase();
      const currentPath = [
        ...parentPath,
        optionValue,
      ];
      const pathKey = currentPath.join('>');
      const isOpen = openPaths.has(pathKey) || (searchQuery && autoOpenPaths.has(pathKey));
      const hasChildren = option.childComponents && option.childComponents.length > 0;
      const isChecked = Array.isArray(selectedValues) && selectedValues.some(
        val => String(val || '').toLowerCase() === optionValue
      );

      return (
        <div
          key={option.value || option.label || index}
          style={{ paddingLeft: parentPath.length * 20 }}
        >
          <Flex>
            {isTree && (
              <Box style={{ paddingLeft: hasChildren ? '10px' : '24px', display: 'flex', alignItems: 'center' }}>
                {hasChildren && (
                  isOpen ? (
                    <IconChevronDown 
                      size={14} 
                      color="#adb5bd" 
                      onClick={() => toggleOpen(pathKey)}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <IconChevronRight 
                      size={14} 
                      color="#adb5bd" 
                      onClick={() => toggleOpen(pathKey)}
                      style={{ cursor: 'pointer' }}
                    />
                  )
                )}
              </Box>
            )}

            <Checkbox
              my={5}
              sx={{
                cursor: 'pointer',
                '& input': {
                  cursor: 'pointer',
                  '&:checked': {
                    backgroundColor: '#5B60E3',
                    borderColor: '#5B60E3',
                  }
                }
              }}
              pr="sm"
              pl="sm"
              py={3}
              value={optionValue}
              label={
                isSourceFilter ? (
                  <Flex align="center" gap={6}>
                    <Avatar radius="xl" size={28}>
                      <SourceIcon sourceName={String(option.value || option.label || '')} width={16} height={16} />
                    </Avatar>
                    <Text size="sm">{capitalizeFirstLetter(String(option.label || option.name || ''))}</Text>
                  </Flex>
                ) : (
                  <Text style={{ display: 'flex', alignItems: 'center' }}>
                    {isProductAreaTree && (
                      <Box style={{ display: 'flex', alignItems: 'center', marginRight: '7px' }}>
                        {option.type === 'domain' ? (
                          <DomainHeaderIcon fill="#adb5bd" color="#adb5bd" />
                        ) : (
                          <ProductAreaHeaderIcon fill="#adb5bd" color="#adb5bd" />
                        )}
                      </Box>
                    )}
                    <Highlight highlight={capitalizeFirstLetter(searchQuery)}>
                      {capitalizeFirstLetter(String(option.label || option.name || ''))}
                    </Highlight>
                  </Text>
                )
              }
            />
          </Flex>
          {hasChildren && isOpen && renderOptions(option.childComponents, currentPath)}
        </div>
      );
    });
  };

  // Auto-open root domain if there's only one and no search
  useEffect(() => {
    if (
      isProductAreaTree &&
      filteredOptions.length === 1 &&
      filteredOptions[0].childComponents &&
      filteredOptions[0].childComponents.length > 0 &&
      !searchQuery
    ) {
      const domain = filteredOptions[0];
      const pathKey = String(domain.value || domain.label || '').toLowerCase();
      if (!openPaths.has(pathKey)) {
        setOpenPaths(prev => new Set(prev).add(pathKey));
      }
    }
  }, [filteredOptions, isProductAreaTree, searchQuery]);

  // Handle checkbox group change for the tree layouts
  const handleGroupChange = (newValues: string[]) => {
    // Compared against the same derived set the checkboxes were rendered from,
    // or unticking a group would look like "nothing changed".
    const previousValues = withDerivedGroupTicks(
      Array.isArray(selectedValues)
        ? selectedValues.map((v: any) => String(v || '').toLowerCase())
        : []
    );
    
    // Find what changed
    const added = newValues.filter(v => !previousValues.includes(v));
    const removed = previousValues.filter(v => !newValues.includes(v));
    
    const toggledValue = added.length > 0 ? added[0] : removed[0];
    const isAdding = added.length > 0;
    const finalValues = applyParentChildToggle(
      processedOptions,
      newValues,
      toggledValue,
      isAdding
    );

    // Convert back to original format (preserve ObjectIds if they were there)
    const formattedValues = withoutGroupValues(finalValues).map((val: string) => {
      // Try to find original value in selectedValues to preserve format
      const original = Array.isArray(selectedValues) 
        ? selectedValues.find((v: any) => String(v || '').toLowerCase() === val)
        : null;
      return original || val;
    });
    
    // Determine the correct filterObject: if filterConfig has filterObject, use it, otherwise use selectedCategory
    // For evidence filters, selectedCategory might be the filter name (like "product-area"), 
    // but filterObject should be "evidence" or "idea" based on the data structure
    const filterObject = filterConfig.filterObject || 'evidence';
    
    // Update with all values
    const updatedFilter: ActiveFilter = {
      ...filterConfig,
      name: filterName,
      filterObject: filterObject as 'evidence' | 'idea',
      selected: formattedValues,
      query: null,
      options: undefined,
    };

    setLocalActiveFilters(prev => {
      const existingIndex = prev.findIndex(
        filter => filter.name === filterName && filter.filterObject === filterObject
      );

      let updatedFilters;
      if (existingIndex >= 0) {
        if (formattedValues.length === 0) {
          updatedFilters = prev.filter((_, index) => index !== existingIndex);
        } else {
          updatedFilters = [...prev];
          updatedFilters[existingIndex] = updatedFilter;
        }
      } else if (formattedValues.length > 0) {
        updatedFilters = [...prev, updatedFilter];
      } else {
        updatedFilters = prev;
      }

      return updatedFilters;
    });

    onCountUpdate?.(formattedValues.length);
  };

  // Regular flat checkbox list
  // For owner filter, apply search filtering
  // NOTE: This must be called before any early returns to satisfy React hooks rules
  const displayOptions = isOwnerFilter ? filteredOptions : processedOptions;
  
  // For owner filter, check if all options are selected (for "Select All")
  // NOTE: This must be called before any early returns to satisfy React hooks rules
  const allOptionsSelected = useMemo(() => {
    if (!isOwnerFilter || displayOptions.length === 0) return false;
    
    const selectedValuesArray = Array.isArray(selectedValues) ? selectedValues : [];
    if (selectedValuesArray.length === 0) return false;
    
    // Check if all display options are in selected values
    return displayOptions.every((option: any) => {
      const optionValue = option.value || option;
      return selectedValuesArray.some(
        val => {
          const valStr = val?.toString().toLowerCase() || '';
          const optStr = optionValue?.toString().toLowerCase() || '';
          return valStr === optStr;
        }
      );
    });
  }, [isOwnerFilter, displayOptions, selectedValues]);

  if (isTree) {
    const selectedValuesArray = withDerivedGroupTicks(
      Array.isArray(selectedValues)
        ? selectedValues.map((v: any) => (v?.toLowerCase() || v?.toString() || v))
        : []
    );
    
    return (
      <Stack spacing="xs">
        <TextInput
          placeholder={`Search ${filterConfig.label || filterName}`}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          icon={<IconListSearch size={14.5} />}
          sx={{
            input: {
              backgroundColor: '#F9FAFB',
              '&:focus': {
                borderColor: '#b1b1b1',
              },
            },
          }}
        />
        <Box style={{ overflowY: 'auto' }}>
          <Checkbox.Group
            value={selectedValuesArray}
            onChange={handleGroupChange}
          >
            <Stack spacing="xs">
              {renderOptions(filteredOptions)}
            </Stack>
          </Checkbox.Group>
        </Box>
      </Stack>
    );
  }
  
  // Options filled in by the dashboard are empty while they load, so only server-listed filters fall back to free text.
  const acceptsFreeText = filterConfig.optionsCapped
    || (processedOptions.length === 0 && !filterConfig.optionsKeyAppContext && !isOwnerFilter);

  if (acceptsFreeText) {
    const selectedArray: string[] = Array.isArray(selectedValues) ? selectedValues.map(String) : [];
    const addFreeTextValue = (): void => {
      const value = freeTextValue.trim();
      if (value && !selectedArray.some((selected) => selected.toLowerCase() === value.toLowerCase())) {
        handleOptionChange(value, true);
      }
      setFreeTextValue('');
    };

    return (
      <Stack spacing="xs" pr={25}>
        <TextInput
          placeholder={`Type a ${filterConfig.label || filterName} value and press Enter`}
          value={freeTextValue}
          onChange={(event) => setFreeTextValue(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addFreeTextValue();
            }
          }}
        />
        {selectedArray.map((value) => (
          <Checkbox
            key={value}
            label={<Text>{value}</Text>}
            checked
            onChange={() => handleOptionChange(value, false)}
            size="sm"
          />
        ))}
      </Stack>
    );
  }

  // Handle Select All for owner filter
  const handleSelectAll = (checked: boolean) => {
    if (!isOwnerFilter) return;
    
    const filterObject = filterConfig.filterObject || 'evidence';
    
    if (checked) {
      // Select all options
      const allValues = displayOptions.map((option: any) => option.value || option);
      
      const updatedFilter: ActiveFilter = {
        ...filterConfig,
        name: filterName,
        filterObject: filterObject as 'evidence' | 'idea',
        selected: allValues,
        query: null,
        options: undefined,
      };

      setLocalActiveFilters(prev => {
        const existingIndex = prev.findIndex(
          filter => filter.name === filterName && filter.filterObject === filterObject
        );

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = updatedFilter;
          return updated;
        } else {
          return [...prev, updatedFilter];
        }
      });
      
      onCountUpdate?.(allValues.length);
    } else {
      // Deselect all - remove the filter if it exists
      setLocalActiveFilters(prev => {
        return prev.filter(
          filter => !(filter.name === filterName && filter.filterObject === filterObject)
        );
      });
      
      onCountUpdate?.(0);
    }
  };
  
  return (
    <Stack 
      spacing="s"
      pr={25}
      sx={{ 
        height: isOwnerFilter ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Add search input for owner filter */}
      {isOwnerFilter && (
        <TextInput
          placeholder="Search..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          icon={<IconSearch size={15} color="#1C1B1F"/>}
          mb={16}
          styles={{
            input: {
              height: '32px',
              minHeight: '32px',
              border: '1px solid #D8D8DB',
              borderRadius: '4px',
              color: '#585C68',
              fontSize: '12px',
            },
          }}
        />
      )}
      
      {/* Select All checkbox for owner filter */}
      {isOwnerFilter && displayOptions.length > 0 && (
        <Checkbox
          label={<Text fw={500}>Select All</Text>}
          checked={allOptionsSelected}
          onChange={(event) => handleSelectAll(event.currentTarget.checked)}
          size="sm"
          sx={{
            cursor: 'pointer',
            paddingBottom: '15px',
            borderBottom: '1px solid #e9ecef',
            flexShrink: 0,
            '& input': {
              cursor: 'pointer',
              '&:checked': {
                backgroundColor: '#5B60E3',
                borderColor: '#5B60E3',
              }
            }
          }}
        />
      )}
      
      {displayOptions.length === 0 && searchQuery ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No owners found matching "{searchQuery}"
        </Text>
      ) : (
        <ScrollArea 
          sx={{ 
            flex: isOwnerFilter ? 1 : 'none',
            minHeight: 0,
          }}
        >
          <Stack spacing="xs">
            {displayOptions.map((option: any) => {
              const optionValue =
                (typeof option?.value === 'string' || typeof option?.value === 'number'
                  ? String(option.value)
                  : (typeof option?._id === 'string' ? option._id : ''));
              const optionLabel =
                (typeof option?.label === 'string'
                  ? option.label
                  : (typeof option?.name === 'string' ? option.name : ''));
              const isChecked = Array.isArray(selectedValues) && selectedValues.some(
                val => (val?.toLowerCase() || val) === (optionValue?.toLowerCase() || optionValue)
              );
              
              return (
                <Checkbox
                  key={optionValue}
                  label={
                    isOwnerFilter ? (
                      <Flex align="center" gap={6}>
                        {optionValue !== 'unassigned' && (
                          <Avatar src={option.picture} radius="xl" size={28}>
                            {typeof option?.name === 'string'
                              ? getInitials(option.name)
                              : (optionLabel ? getInitials(optionLabel) : '')}
                          </Avatar>
                        )}
                        <Stack spacing={0}>
                          <Text size="sm">
                            <Highlight highlight={searchQuery}>
                              {optionLabel}
                            </Highlight>
                          </Text>
                          {option.email && !option.isMe && (
                            <Text size="xs" c="dimmed">
                              <Highlight highlight={searchQuery}>
                                {option.email}
                              </Highlight>
                            </Text>
                          )}
                        </Stack>
                      </Flex>
                    ) : isSourceFilter ? (
                      <Flex align="center" gap={6}>
                        <Avatar radius="xl" size={28}>
                          <SourceIcon sourceName={optionValue} width={16} height={16} />
                        </Avatar>
                        <Text size="sm">{optionLabel}</Text>
                      </Flex>
                    ) : (
                      <Text>
                        <Highlight highlight={searchQuery}>
                          {optionLabel}
                        </Highlight>
                      </Text>
                    )
                  }
                  checked={isChecked}
                  onChange={(event) => handleOptionChange(optionValue, event.currentTarget.checked)}
                  size="sm"
                  styles={isOwnerFilter ? {
                    body: {
                      alignItems: 'center',
                    }
                  } : undefined}
                  sx={{
                    cursor: 'pointer',
                    '& input': {
                      cursor: 'pointer',
                      '&:checked': {
                        backgroundColor: '#5B60E3',
                        borderColor: '#5B60E3',
                      }
                    }
                  }}
                />
              );
            })}
          </Stack>
        </ScrollArea>
      )}
    </Stack>
  );
};

export default CheckboxFilterForModal;
